/**
 * Notifications Queries
 * Functions for managing in-app notifications
 */

import { supabase } from "../supabase-client";

export type NotificationType =
  | "consultation_new"
  | "consultation_accepted"
  | "consultation_rejected"
  | "consultation_completed"
  | "case_assigned"
  | "case_updated"
  | "document_generated"
  | "document_reviewed"
  | "review_received"
  | "payment_received"
  | "simulation_graded"
  | "badge_earned"
  | "message_received"
  | "system"
  | "reminder"
  | "verification_approved"
  | "verification_rejected"
  | "exequatur_invalid"
  | "missing_exequatur";

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  message: string;
  data: Record<string, any>;
  link: string | null;
  is_read: boolean;
  is_archived: boolean;
  created_at: string;
  read_at: string | null;
}

export interface CreateNotificationInput {
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  data?: Record<string, any>;
  link?: string;
}

/**
 * Create a notification for a user (in-app)
 * Respects user notification_preferences. Returns null if user disabled this type.
 */
export async function createNotification(
  input: CreateNotificationInput
): Promise<string | null> {
  const { data, error } = await supabase.rpc("create_notification", {
    p_user_id: input.userId,
    p_type: input.type,
    p_title: input.title,
    p_message: input.message,
    p_data: input.data || {},
    p_link: input.link,
  });

  if (error) {
    throw new Error(`Error creating notification: ${error.message}`);
  }

  return data as string | null; // null when user disabled this notification type
}

/**
 * Get notifications for current user
 */
export async function getNotifications(options?: {
  limit?: number;
  offset?: number;
  unreadOnly?: boolean;
}): Promise<Notification[]> {
  let query = supabase
    .from("notifications")
    .select("*")
    .order("created_at", { ascending: false });

  if (options?.unreadOnly) {
    query = query.eq("is_read", false).eq("is_archived", false);
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  if (options?.offset) {
    query = query.range(
      options.offset,
      options.offset + (options.limit || 10) - 1
    );
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Error fetching notifications: ${error.message}`);
  }

  return (data as Notification[]) || [];
}

/**
 * Get unread notification count
 */
export async function getUnreadNotificationCount(): Promise<number> {
  const { data, error } = await supabase.rpc("get_unread_notification_count");

  if (error) {
    console.error("Error getting unread count:", error);
    return 0;
  }

  return data as number;
}

/**
 * Mark a notification as read
 */
export async function markNotificationRead(
  notificationId: string
): Promise<void> {
  const { error } = await supabase.rpc("mark_notification_read", {
    p_notification_id: notificationId,
  });

  if (error) {
    throw new Error(`Error marking notification as read: ${error.message}`);
  }
}

/**
 * Mark all notifications as read
 */
export async function markAllNotificationsRead(): Promise<number> {
  const { data, error } = await supabase.rpc("mark_all_notifications_read");

  if (error) {
    throw new Error(
      `Error marking all notifications as read: ${error.message}`
    );
  }

  return data as number;
}

/**
 * Archive a notification
 */
export async function archiveNotification(
  notificationId: string
): Promise<void> {
  const { error } = await supabase
    .from("notifications")
    .update({ is_archived: true })
    .eq("id", notificationId);

  if (error) {
    throw new Error(`Error archiving notification: ${error.message}`);
  }
}

/**
 * Delete a notification
 */
export async function deleteNotification(
  notificationId: string
): Promise<void> {
  const { error } = await supabase
    .from("notifications")
    .delete()
    .eq("id", notificationId);

  if (error) {
    throw new Error(`Error deleting notification: ${error.message}`);
  }
}

/**
 * Subscribe to real-time notifications
 */
export function subscribeToNotifications(
  userId: string,
  callback: (notification: Notification) => void
) {
  const channel = supabase
    .channel("notifications")
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "notifications",
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        callback(payload.new as Notification);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * Get notifications by type
 */
export async function getNotificationsByType(
  type: NotificationType,
  limit = 10
): Promise<Notification[]> {
  const { data, error } = await supabase
    .from("notifications")
    .select("*")
    .eq("type", type)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) {
    throw new Error(`Error fetching notifications by type: ${error.message}`);
  }

  return (data as Notification[]) || [];
}

/**
 * Cleanup old read notifications (admin function)
 */
export async function cleanupOldNotifications(): Promise<number> {
  const { data, error } = await supabase.rpc("cleanup_old_notifications");

  if (error) {
    throw new Error(`Error cleaning up notifications: ${error.message}`);
  }

  return data as number;
}
